import { HTTPError } from "ky";
import * as z from "zod";
import { api } from "@/lib/http";

export const policyTypeSchema = z.enum(["Blocklist", "Redactlist"]);

export const affectedEntitySchema = z.object({
  id: z.number(),
  name: z.string(),
  required_policy_mode: policyTypeSchema,
});

export const missingEntityDetailSchema = z.object({
  entity_id: z.int(),
  entity_name: z.string(),
  source_policy_id: z.int(),
  source_policy_type: policyTypeSchema,
  source_policy_priority: z.int(),
  type: z.enum(["not_existing", "needs_upgrade"]),
});
export const affectedKeySchema = z.object({
  key_id: z.string(),
  key_name: z.string(),
  key_description: z.string().nullable().optional(),
  user_id: z.string(),
  user_name: z.string(),
  user_email: z.string(),
  missing_entities: z.array(affectedEntitySchema),
  missing_entity_details: z.array(missingEntityDetailSchema),
});

export const gatewayKeyConflictDetailSchema = z.object({
  code: z.literal("GATEWAY_KEY_CONFLICTS"),
  message: z.string(),
  affected_keys: z.array(affectedKeySchema),
});

export type AffectedEntity = z.infer<typeof affectedEntitySchema>;
export type AffectedKey = z.infer<typeof affectedKeySchema>;
export type GatewayKeyConflictDetail = z.infer<
  typeof gatewayKeyConflictDetailSchema
>;

export const GATEWAY_KEY_CONFLICT_ACTIONS = [
  "add_entities",
  "delete",
  "skip",
] as const;
export type GatewayKeyConflictAction =
  (typeof GATEWAY_KEY_CONFLICT_ACTIONS)[number];

export type GatewayKeyResolution = {
  key_id: string;
  action: GatewayKeyConflictAction;
};

export type ApiMethod = "POST" | "PATCH" | "PUT" | "DELETE";

export type ApiRequestDescriptor = {
  method: ApiMethod;
  url: string;
  payload: Record<string, unknown>;
  kind: string;
  label?: string;
};

export type PendingPolicyConflict = {
  returnUrl: string;
  apiRequest: ApiRequestDescriptor;
  /** Additional requests to run sequentially after `apiRequest` succeeds. */
  followUps?: Array<ApiRequestDescriptor>;
  /** Optional toast message shown once the whole batch completes. */
  successToast?: string;
  conflict: GatewayKeyConflictDetail;
  /**
   * Optional label used by the UI when the primary request and follow-ups are
   * part of the same batch but no `successToast` was provided. Back-compat
   * alias for older callers that used `apiRequest.policyName`.
   */
  batchLabel?: string;
};

const STORAGE_KEY = "polygraf_pending_policy_conflict";

export function savePendingPolicyConflict(data: PendingPolicyConflict): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function loadPendingPolicyConflict(): PendingPolicyConflict | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingPolicyConflict;
  } catch {
    return null;
  }
}

export function clearPendingPolicyConflict(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function updatePendingPolicyConflictDetail(
  conflict: GatewayKeyConflictDetail,
): void {
  const cur = loadPendingPolicyConflict();
  if (!cur) return;
  savePendingPolicyConflict({ ...cur, conflict });
}

export async function parseGatewayKeyConflict(
  err: unknown,
): Promise<GatewayKeyConflictDetail | null> {
  if (!(err instanceof HTTPError)) return null;
  if (err.response.status !== 409) return null;
  const body = await err.response.json().catch(() => null);
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  const parsed = gatewayKeyConflictDetailSchema.safeParse(detail);
  return parsed.success ? parsed.data : null;
}

async function issueApiRequest(
  req: ApiRequestDescriptor,
  extras?: Record<string, unknown>,
): Promise<unknown> {
  const body = { ...req.payload, ...(extras ?? {}) };
  switch (req.method) {
    case "POST":
      return api.raw.post(req.url, { json: body }).json();
    case "PATCH":
      return api.raw.patch(req.url, { json: body }).json();
    case "PUT":
      return api.raw.put(req.url, { json: body }).json();
    case "DELETE":
      return api.raw.delete(req.url, { json: body }).json();
  }
}

export type ReplayResult =
  | { status: "success" }
  | { status: "conflict"; pending: PendingPolicyConflict };

/**
 * Replays the pending conflicted request with the user's resolutions, then
 * runs any queued follow-up requests sequentially. If any of those steps
 * returns another 409, saves an updated `PendingPolicyConflict` to session
 * and resolves with `{ status: "conflict" }` so the caller can re-render the
 * resolution UI. Any non-409 failure is thrown.
 */
export async function replayPendingPolicyConflict(
  pending: PendingPolicyConflict,
  resolutions: Array<GatewayKeyResolution>,
): Promise<ReplayResult> {
  try {
    await issueApiRequest(pending.apiRequest, {
      conflict_resolutions: resolutions,
    });
  } catch (err) {
    const conflict = await parseGatewayKeyConflict(err);
    if (conflict) {
      const next: PendingPolicyConflict = { ...pending, conflict };
      savePendingPolicyConflict(next);
      return { status: "conflict", pending: next };
    }
    throw err;
  }

  const followUps = pending.followUps ?? [];
  for (let i = 0; i < followUps.length; i++) {
    const req = followUps[i];
    try {
      await issueApiRequest(req);
    } catch (err) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        const next: PendingPolicyConflict = {
          ...pending,
          apiRequest: req,
          followUps: followUps.slice(i + 1),
          conflict,
        };
        savePendingPolicyConflict(next);
        return { status: "conflict", pending: next };
      }
      throw err;
    }
  }

  return { status: "success" };
}

export type SequenceResult =
  | { status: "success" }
  | { status: "conflict"; pending: PendingPolicyConflict };

/**
 * Runs a batch of API requests sequentially. If any request returns a 409
 * gateway-key conflict, saves a `PendingPolicyConflict` containing the
 * failed request plus every remaining request as `followUps`, and resolves
 * with `{ status: "conflict" }`. Non-409 errors are thrown.
 */
export async function runConflictAwareSequence(
  requests: Array<ApiRequestDescriptor>,
  meta: {
    returnUrl: string;
    successToast?: string;
    batchLabel?: string;
  },
): Promise<SequenceResult> {
  for (let i = 0; i < requests.length; i++) {
    const req = requests[i];
    try {
      await issueApiRequest(req);
    } catch (err) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        const pending: PendingPolicyConflict = {
          returnUrl: meta.returnUrl,
          apiRequest: req,
          followUps: requests.slice(i + 1),
          successToast: meta.successToast,
          batchLabel: meta.batchLabel,
          conflict,
        };
        savePendingPolicyConflict(pending);
        return { status: "conflict", pending };
      }
      throw err;
    }
  }
  return { status: "success" };
}
